import logoSvgRaw from '../assets/logo-sportadmin.svg?raw';
import { useId, useMemo } from 'react';

// LogoLoader renders the SportAdmin wordmark with a continuous hand-drawn
// animation: stroke traces each path (~1.6s), fill fades in (~0.4s), holds
// briefly, then resets and repeats. The keyframes live in global.css under
// .logo-loader so the animation reuses the same easing tokens as the rest
// of the design system.
//
// The SVG is loaded as raw text via Vite's ?raw suffix so we can dangerously
// inline it AFTER tagging every <path> with `pathLength="100"`. Normalising
// the path length means a single CSS keyframe (stroke-dashoffset 100→0)
// works for every path regardless of its real geometry — no per-path JS
// measurement needed.
//
// Implementation notes:
//   - We append a unique class to each path so animation-delay can stagger
//     them slightly. Without staggering, all 12 paths draw in lockstep,
//     which looks rigid; a small per-path delay creates the "the writer is
//     moving across the wordmark" feel.
//   - dangerouslySetInnerHTML is safe here: the SVG is bundled at build
//     time, not user-supplied. Same risk profile as importing a .svg as a
//     React component via vite-plugin-svgr.
//   - prefers-reduced-motion: handled in CSS — the keyframe collapses to a
//     gentle opacity pulse so motion-sensitive users still get a "working"
//     signal without the moving stroke.

export interface LogoLoaderProps {
  // size in CSS units (default: 240px width). Height auto-scales via the SVG
  // viewBox aspect ratio (529:100).
  size?: number | string;
  // a11y label announced to screen readers via aria-label on the wrapper.
  // Defaults to "Loading…" — pass the localised string from the caller.
  label?: string;
  className?: string;
}

export function LogoLoader({ size = 240, label = 'Loading…', className }: LogoLoaderProps) {
  // useId gives us a stable unique id per render so multiple LogoLoaders on
  // one page don't share the same per-path classes (which would cancel
  // each other's animation phases).
  const uid = useId().replace(/[:]/g, '');

  // Inject pathLength="100" + a unique stagger class on every <path>.
  // Doing it via string replace keeps the source SVG intact for static use
  // while letting this component drive the animation. The regex is anchored
  // to <path tags only so we don't accidentally rewrite other markup.
  const html = useMemo(() => {
    let i = 0;
    return logoSvgRaw.replace(/<path /g, () => {
      i += 1;
      return `<path pathLength="100" class="logo-loader__path logo-loader__path--${i} logo-loader__path--${uid}-${i}" `;
    });
  }, [uid]);

  // Per-path animation-delay tags — generated for the unique uid so multiple
  // LogoLoader instances animate independently.
  const staggerStyles = useMemo(() => {
    const max = 13; // 12 letters + symbol; one extra is harmless
    let css = '';
    for (let n = 1; n <= max; n++) {
      // Cap at 1.2s lead so the wave finishes within one full cycle.
      const delayMs = Math.min((n - 1) * 90, 1200);
      css += `.logo-loader__path--${uid}-${n}{animation-delay:${delayMs}ms}`;
    }
    return css;
  }, [uid]);

  const width = typeof size === 'number' ? `${size}px` : size;

  return (
    <div
      className={`logo-loader${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      aria-label={label}
      style={{ width, maxWidth: '100%' }}
    >
      <style>{staggerStyles}</style>
      {/* Inlining the SVG (rather than <img src>) is what gives us CSS
          control over <path> attributes — required for the
          stroke-dashoffset animation. */}
      <div
        className="logo-loader__svg"
        // eslint-disable-next-line react/no-danger -- SVG is bundled at build time
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}
